import { Component, OnInit } from '@angular/core';
import { ColDef, GridReadyEvent, SelectionChangedEvent } from 'ag-grid-community';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { JsignalService } from 'src/app/shared/services/jsignal.service';
import { AgGridActionbutton } from 'src/app/shared/common/AgGridActionbutton';
import { JSIGNALBUTTON } from 'src/app/shared/common/JsignalButtons';
import { GenericBadgeRendererComponent } from 'src/app/shared/common/generic-badge-renderer.component';
import { DeviationComponent } from '../../deviation/deviation.component';
import { UserRoutingModule } from '../../user-routing/user-routing.module';
import { Router } from '@angular/router';

@Component({
    selector: 'app-ha-request-management',
    templateUrl: './ha-request-management.component.html',
    styleUrls: ['./ha-request-management.component.scss'],
    standalone: false
})
export class HaRequestManagementComponent implements OnInit {

  /* =========================================================
      VIEW
  ========================================================= */

  viewMode: 'LIST' | 'FORM' = 'LIST';
  selectedRequestId: number | null = null;

  /* =========================================================
      GRID
  ========================================================= */

  rowData: any[] = [];
  selectedRows: any[] = [];
  gridApi: any;

  components = {
    jsignalButton: JSIGNALBUTTON,
    buttonRenderer: AgGridActionbutton,
    badgeRenderer: GenericBadgeRendererComponent
  };

columnDefs: ColDef[] = [

  {
    headerName: 'ID',
    field: 'id',
    width: 100,
    minWidth: 100,
    ...this.jsignalService.autoTooltip('ID')
  },

  {
    headerName: 'Source',
    field: 'source',
    width: 180,
    minWidth: 180,
    ...this.jsignalService.autoTooltip('Source')
  },

  {
    headerName: 'Subject',
    field: 'subject',
    width: 250,
    minWidth: 250,
    ...this.jsignalService.autoTooltip('Subject')
  },

  {
    headerName: 'Product',
    field: 'productName',
    width: 220,
    minWidth: 220,
    ...this.jsignalService.autoTooltip('Product')
  },

  {
    headerName: 'Due Date',
    field: 'dueDate',
    width: 170,
    minWidth: 170,
    ...this.jsignalService.autoTooltip('HA Due Date')
  },

  {
    headerName: 'Status',
    field: 'status',
    width: 130,
    minWidth: 130,
    ...this.jsignalService.autoTooltip('Status'),
    cellRenderer: GenericBadgeRendererComponent,
    cellRendererParams: { type: 'YES_NO' }
  },

  {
    headerName: 'State',
    field: 'state',
    width: 180,
    minWidth: 180,
    ...this.jsignalService.autoTooltip('State')
  },

  {
    headerName: 'External Tracking ID',
    field: 'externalTrackingId',
    width: 250,
    minWidth: 250,
    ...this.jsignalService.autoTooltip('External Tracking ID')
  },

  {
    headerName: 'Assigned To',
    field: 'assignedTo',
    width: 220,
    minWidth: 220,
    ...this.jsignalService.autoTooltip('Assigned To')
  },
{
    headerName: 'Deviation Id',
    field: 'deviationId',
    width: 220,
    minWidth: 220,
    ...this.jsignalService.autoTooltip('Deviation Id'),

    cellRenderer: 'jsignalButton',

    cellRendererParams: {
      deviationLink: true,
      getDeviationUrl: (params) =>
        this.getDeviationUrl(params)
    }
  },
  {
    headerName: 'Deviation Code',
    field: 'deviationCode',
    width: 220,
    minWidth: 220,
    ...this.jsignalService.autoTooltip('Deviation Code'),
  },

  {
    headerName: 'Deviation State',
    field: 'deviationState',
    width: 220,
    minWidth: 220,
    ...this.jsignalService.autoTooltip('Deviation State')
  },

  {
    headerName: 'Deviation Closure Date',
    field: 'deviationClosureDate',
    width: 240,
    minWidth: 240,
    ...this.jsignalService.autoTooltip('Deviation Closure Date')
  },

  {
    headerName: 'Actions',
    width: 260,
    minWidth: 260,
    pinned: 'right',

    cellRenderer: 'buttonRenderer',

    cellRendererParams: (params: any) => {

      const row = params.data;

      const isOverdue =
        this.isOverdue(row);

      const isDisabled =
        this.isRequestDisabled(row.state);

      return {

        showEdit: !isDisabled,

        showDelete: !isDisabled,

        showDeviation: true,

        showAssign: !isDisabled,

        deviationDisabled:
          !isOverdue || isDisabled,

        assignDisabled:
          !this.canAssign(row),

        edit: (e: any) =>
          this.onEditRow(e),

        delete: (e: any) =>
          this.onDeleteRow(e),

        userAssign: (e: any) =>
          this.onUserAssign(e),

        deviation: (e: any) =>
          this.onDeviationCreation(e)
      };
    }
  }
];
  defaultColDef: ColDef = {
   sortable: true,
      filter: true,
      resizable: true,
      autoHeight: true,
      filterParams: {
        maxNumConditions: 2,
        numAlwaysVisibleConditions: 2,
        defaultJoinOperator: 'OR'
      }
  };

  /* =========================================================
      SUMMARY COUNTS
  ========================================================= */

  newCount = 0;
  inProgressCount = 0;
  completedCount = 0;
  cancelledCount = 0;

  readonly DISABLED_STATES = ['Completed', 'Cancelled'];

  /* =========================================================
      CONSTRUCTOR
  ========================================================= */

  constructor(
    private jsignalService: JsignalService,
    private snackBar: MatSnackBar,
    private spinner: NgxUiLoaderService,
    private dialog: MatDialog,
    private userRoutingModule: UserRoutingModule,
    private router: Router
  ) {}

  /* =========================================================
      INIT
  ========================================================= */

  ngOnInit(): void {
    this.loadAllData();
  }

  /* =========================================================
      HELPER METHODS
  ========================================================= */

  isRequestDisabled(state: string): boolean {
    return this.DISABLED_STATES.includes(state);
  }

  /* =========================================================
      LOAD ALL
  ========================================================= */

  loadAllData(): void {
    this.loadSummary();
    this.loadRequests();
  }

  /* =========================================================
      LOAD REQUESTS
  ========================================================= */

  loadRequests(): void {
    this.spinner.start();
    this.jsignalService.getAllHaRequests().subscribe({
      next: (res: any) => {
        const data = res?.data || res || [];
        this.rowData = data.map((item: any) => this.mapRequestForDisplay(item));
        this.spinner.stop();
      },
      error: (err) => {
        console.error('Error loading requests:', err);
        this.snackBar.open(
          err.error?.statusMessage || err.error?.message || 'Failed to load requests',
          'OK',
          { duration: 3000 }
        );
        this.spinner.stop();
      }
    });
  }

  mapRequestForDisplay(item: any): any {
    return {
      id: item.id,
      source: item.source || '-',
      subject: item.subject || '-',
      productName: item.productName || '-',
      status: item.status || 'No',
      state: item.state || '-',
      assignedTo: item.assignedTo || 'Unassigned',
      externalTrackingId: item.externalTrackingId || '-',
      deviationId: item.deviationId,
      deviationCode: item.deviationCode,
      deviationState: item.deviationState,
      deviationClosureDate: item.deviationClosureDate,
      userID: item.userID ? String(item.userID) : '',
      dueDate: item.dueDate,
      issueDate: item.issueDate,
      createdDate: item.createdDate,
      productId: item.productId,
      description: item.description,
      inn: item.inn,
      productStatus: item.productStatus,
      productType: item.productType,
      cancelReason: item.cancelReason,
      workspaceid: item.workspaceid,
      dnsid: item.dnsid,
      createdBy: item.createdBy,
      lastModifiedBy: item.lastModifiedBy,
      lastModifiedDate: item.lastModifiedDate
    };
  }

  /* =========================================================
      LOAD SUMMARY
  ========================================================= */

  loadSummary(): void {
    this.jsignalService.getHaRequestSummary().subscribe({
      next: (summary: any) => {
        this.newCount = summary?.newCount || 0;
        this.inProgressCount = summary?.inProgressCount || 0;
        this.completedCount = summary?.completedCount || 0;
        this.cancelledCount = summary?.cancelledCount || 0;
      },
      error: (err) => {
        console.error('Error loading summary:', err);
      }
    });
  }

  /* =========================================================
      GRID EVENTS
  ========================================================= */

 onGridReady(params: GridReadyEvent): void {
  this.gridApi = params.api;
}

  onSelectionChanged(event: SelectionChangedEvent): void {
    this.selectedRows = event.api.getSelectedRows();
  }

  /* =========================================================
      CREATE
  ========================================================= */

  onCreate(): void {
    this.selectedRequestId = null;
    this.viewMode = 'FORM';
  }

  /* =========================================================
      EDIT
  ========================================================= */

  onEditRow(event: any): void {
    const row = event.rowData;
    const currentUser = this.getCurrentUser();

    if (row.status === 'No') {
      this.snackBar.open(`Request ${row.id} is Deleted`, 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (this.isRequestDisabled(row.state)) {
      this.snackBar.open(`Cannot edit request in ${row.state} state`, 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (!row.assignedTo || row.assignedTo === 'Unassigned') {
      this.snackBar.open('Please assign this request to yourself first to edit', 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (row.userID !== currentUser) {
      this.snackBar.open('You can edit only records assigned to you', 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.selectedRequestId = row.id;
    this.viewMode = 'FORM';
  }

  /* =========================================================
      DELETE
  ========================================================= */

  onDeleteRow(event: any): void {
    const row = event.rowData;
    const currentUser = this.getCurrentUser();

    if (row.status === 'No') {
      this.snackBar.open(`Request ${row.id} is already deleted`, 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (this.isRequestDisabled(row.state)) {
      this.snackBar.open(`Cannot delete request in ${row.state} state`, 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (!row.assignedTo || row.assignedTo === 'Unassigned') {
      this.snackBar.open('Please assign this request to yourself first to delete', 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (row.userID !== currentUser) {
      this.snackBar.open('You can delete only records assigned to you', 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (!confirm(`Are you sure you want to delete Request ID: ${row.id}?`)) return;

    this.spinner.start();
    this.jsignalService.deleteHaRequest(row.id).subscribe({
      next: (response: any) => {
        this.spinner.stop();
        const message = response?.statusMessage || response?.message || 'Request deleted successfully';
        const isError = response?.error === true;
        
        if (isError) {
          this.snackBar.open(message, 'OK', { duration: 3000, panelClass: ['error-snackbar'] });
        } else {
          this.snackBar.open(message, 'OK', { duration: 3000 });
          this.loadAllData();
        }
      },
      error: (error) => {
        this.spinner.stop();
        const errorMessage = error.error?.statusMessage || error.error?.message || 'Failed to delete request';
        this.snackBar.open(errorMessage, 'OK', { duration: 3000, panelClass: ['error-snackbar'] });
        console.error('Delete error:', error);
      }
    });
  }

  /* =========================================================
      ASSIGN USER
  ========================================================= */

  onUserAssign(event: any): void {
    const rowData = event.rowData;
    const requestId = rowData.id;
    const currentUser = this.getCurrentUser();

    if (rowData.status === 'No') {
      this.snackBar.open('Cannot assign deleted record', 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (this.isRequestDisabled(rowData.state)) {
      this.snackBar.open(`Cannot assign request in ${rowData.state} state`, 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (!rowData.assignedTo || rowData.assignedTo === 'Unassigned') {
      this.callAssignAPI(requestId, currentUser);
      return;
    }

    if (rowData.userID !== currentUser) {
      const confirmed = window.confirm(
        `This request is already assigned to ${rowData.assignedTo}. Do you want to self-assign?`
      );
      if (confirmed) {
        this.callAssignAPI(requestId, currentUser);
      }
      return;
    }

    this.snackBar.open('Request already assigned to you', 'OK', { duration: 3000 });
  }

  private callAssignAPI(requestId: number, userId: string): void {
    this.spinner.start();
    this.jsignalService.assignUserToHA(requestId, userId).subscribe({
      next: (response: any) => {
        this.spinner.stop();
        const message = response?.statusMessage || response?.message || 'Request assigned successfully';
        const isError = response?.error === true;
        
        if (isError) {
          this.snackBar.open(message, 'OK', { duration: 3000, panelClass: ['error-snackbar'] });
        } else {
          this.snackBar.open(message, 'OK', { duration: 3000 });
          this.loadAllData();
        }
      },
      error: (error) => {
        this.spinner.stop();
        const errorMessage = error.error?.statusMessage || error.error?.message || 'Failed to assign request';
        this.snackBar.open(errorMessage, 'OK', { duration: 3000, panelClass: ['error-snackbar'] });
        console.error('Assignment error:', error);
      }
    });
  }

  /* =========================================================
      BACK
  ========================================================= */

  onBack(): void {
    this.viewMode = 'LIST';
    this.loadAllData();
  }

  /* =========================================================
      OVERDUE CHECK
  ========================================================= */

  isOverdue(row: any): boolean {
    if (!row?.dueDate) return false;
    if (row.status === 'No') return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(row.dueDate);
    due.setHours(0, 0, 0, 0);

    return due < today && row.state !== 'Completed' && row.state !== 'Cancelled';
  }

  /* =========================================================
      ASSIGN CHECK
  ========================================================= */

  canAssign(row: any): boolean {
    return row.state === 'New' || !row.assignedTo || row.assignedTo === 'Unassigned';
  }

  /* =========================================================
      DEVIATION
  ========================================================= */

  onDeviationCreation(event: any): void {
    const rowData = event.rowData;

    if (rowData.status === 'No') {
      this.snackBar.open('Deviation cannot be created for deleted records', 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (this.isRequestDisabled(rowData.state)) {
      this.snackBar.open(`Cannot create deviation for request in ${rowData.state} state`, 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (!this.isOverdue(rowData)) {
      this.snackBar.open('Deviation can be created only for overdue requests', 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (rowData.deviationState === 'COMPLETED') {
      this.snackBar.open('Deviation already completed for this request', 'OK', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    const dialogRef = this.dialog.open(DeviationComponent, {
      maxWidth: '100vw',
      height: '97%',
      width: '90%',
      disableClose: true,
      data: {
        deviationFor: 'HA_REQUEST',
        uniqueRecordId: rowData.id,
        deviationId: rowData.deviationId || null,
        productName: rowData.productName,
        productId: rowData.productId || null
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.loadAllData();
      }
    });
  }

  /* =========================================================
      CURRENT USER
  ========================================================= */

  private getCurrentUser(): string {
    return localStorage.getItem('UserId') || 'Unknown';
  }

  /* =========================================================
      DEVIATION NAVIGATION
  ========================================================= */

  getDeviationUrl(params: any): string {
    const deviationCode = params.rowData?.deviationCode;
    if (!deviationCode) return '#';

    const screenPath = this.userRoutingModule.print('DeviationManagementComponent');
    return this.router.serializeUrl(
      this.router.createUrlTree(['/user/Menubar', screenPath, deviationCode])
    );
  }

  /* =========================================================
      EXPORT CSV
  ========================================================= */

  onDownload(): void {
    if (!this.gridApi || !this.rowData || this.rowData.length === 0) {
      this.snackBar.open('No data to export', 'Close', { duration: 3000 });
      return;
    }

    this.spinner.start();
    setTimeout(() => {
      this.gridApi.exportDataAsCsv({
        fileName: `HA_Requests_${new Date().toISOString().split('T')[0]}.csv`
      });
      this.spinner.stop();
      this.snackBar.open('Export completed successfully', 'OK', { duration: 3000 });
    }, 500);
  }

  /* =========================================================
      REFRESH DATA
  ========================================================= */

  refreshData(): void {
    this.snackBar.open('Refreshing data...', 'OK', { duration: 500 });
    this.loadAllData();
  }
}